import { useEffect, useMemo, useRef, useState } from 'react';
import { AppFieldGroup, AppInput, AppText } from '../../ui-foundation';
import {
  formatAgeLabel,
  resolveDateOfBirthInput,
  sanitizeDatePartInput,
  splitDateOfBirthParts,
  type DateOfBirthParts,
} from '../../ui-foundation/features/family';

type UkDateFieldProps = {
  label: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  blankHint?: string;
  savedAge?: number | null;
};

const EMPTY_PARTS: DateOfBirthParts = {
  day: '',
  month: '',
  year: '',
};

export default function UkDateField({
  label,
  value,
  onChange,
  blankHint = 'Add day, month, and year to let Adulting calculate the age.',
  savedAge,
}: UkDateFieldProps) {
  const [parts, setParts] = useState<DateOfBirthParts>(() => splitDateOfBirthParts(value));
  const skipNextSyncRef = useRef(false);

  useEffect(() => {
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    setParts(splitDateOfBirthParts(value));
  }, [value]);

  const resolved = useMemo(() => resolveDateOfBirthInput(parts), [parts]);

  const helper = useMemo(() => {
    if (resolved.isValid) {
      return {
        tone: 'default' as const,
        text: `Age ${resolved.age}`,
      };
    }

    if (resolved.error) {
      return {
        tone: 'error' as const,
        text: resolved.error,
      };
    }

    if (!resolved.isBlank) {
      return {
        tone: 'muted' as const,
        text: 'Finish day, month, and year to save this date.',
      };
    }

    const savedAgeLabel = formatAgeLabel(value, savedAge);
    if (savedAgeLabel && !value) {
      return {
        tone: 'muted' as const,
        text: savedAge && savedAge > 0 ? `Saved ${savedAgeLabel.toLowerCase()}` : savedAgeLabel,
      };
    }

    return {
      tone: 'muted' as const,
      text: blankHint,
    };
  }, [blankHint, resolved.error, resolved.isBlank, resolved.isValid, resolved.age, savedAge, value]);

  const updatePart = (field: keyof DateOfBirthParts, nextValue: string, maxLength: number) => {
    const sanitized = sanitizeDatePartInput(nextValue, maxLength);
    const nextParts = {
      ...parts,
      [field]: sanitized,
    };
    setParts(nextParts);
    skipNextSyncRef.current = true;
    onChange(resolveDateOfBirthInput(nextParts).isoValue);
  };

  return (
    <AppFieldGroup label={label}>
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <AppText fontSize="$xs" color="$colorMuted">Day</AppText>
            <AppInput
              aria-label={`${label} day`}
              autoComplete="bday-day"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="DD"
              spellCheck={false}
              maxLength={2}
              value={parts.day}
              onChange={(event) => updatePart('day', event.target.value, 2)}
            />
          </div>
          <div className="space-y-1">
            <AppText fontSize="$xs" color="$colorMuted">Month</AppText>
            <AppInput
              aria-label={`${label} month`}
              autoComplete="bday-month"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="MM"
              spellCheck={false}
              maxLength={2}
              value={parts.month}
              onChange={(event) => updatePart('month', event.target.value, 2)}
            />
          </div>
          <div className="space-y-1">
            <AppText fontSize="$xs" color="$colorMuted">Year</AppText>
            <AppInput
              aria-label={`${label} year`}
              autoComplete="bday-year"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="YYYY"
              spellCheck={false}
              maxLength={4}
              value={parts.year}
              onChange={(event) => updatePart('year', event.target.value, 4)}
            />
          </div>
        </div>
        <p className={`text-xs leading-relaxed ${helper.tone === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
          {helper.text}
        </p>
      </div>
    </AppFieldGroup>
  );
}
